/* eslint-disable max-lines-per-function --
   TODO: 招募前 sprint 拆組件 (TODO-frontend-eslint-ci-warnings-as-errors)。
   2026-02-06 規則加入時 87 個既有違規凍結為 baseline，新檔/新函數仍受 hook 攔截。
*/
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { getRoleLabel } from '../../constants/roles';
import FolderIcon from '@mui/icons-material/Folder';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import ChangePasswordDialog from './ChangePasswordDialog';

/**
 * 側邊欄組件
 *
 * 功能：
 * - 顯示主要功能選單
 * - 根據使用者角色顯示不同選單項目
 * - 支援桌面版固定顯示和手機版抽屜式顯示
 *
 * Props:
 * @param {boolean} open - 側邊欄是否開啟（手機版）
 * @param {Function} onClose - 關閉側邊欄的回調函數（手機版）
 * @param {Object} user - 使用者物件 { username, role }
 * @param {boolean} permanent - 是否為永久顯示模式（桌面版）
 */
const Sidebar = ({ open = false, onClose, user = null, variant = 'temporary' }) => {
  const navigate = useNavigate();
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const drawerWidth = 240;

  // 處理選單項目點擊
  const handleMenuClick = (path) => {
    navigate(path);
    if (onClose) {
      onClose(); // 關閉手機版側邊欄
    }
  };

  // 選單項目配置
  const menuItems = [
    // 基本功能（所有使用者）
    {
      title: '搜尋',
      icon: <SearchIcon />,
      path: '/search',
      roles: ['user', 'research', 'advanced', 'admin'],
    },
    // 註：搜尋歷史和我的書籤功能尚未實作，暫時移除
  ];

  // 進階功能（進階使用者和管理員）
  const advancedItems = [
    {
      title: '上傳檔案',
      icon: <UploadFileIcon />,
      path: '/upload',
      roles: ['advanced', 'admin'],
    },
    {
      title: '我的上傳',
      icon: <FolderIcon />,
      path: '/my-uploads',
      roles: ['advanced', 'admin'],
    },
    {
      title: '合併上傳',
      icon: <MergeTypeIcon />,
      path: '/upload/word-merge',
      roles: ['advanced', 'admin'],
    },
    {
      title: '分詞辭典',
      icon: <MenuBookIcon />,
      path: '/dictionary',
      roles: ['advanced', 'admin'],
    },
  ];

  // 管理功能（僅管理員）
  const adminItems = [
    {
      title: '使用者管理',
      icon: <PersonIcon />,
      path: '/admin?tab=0',
      roles: ['admin'],
    },
    {
      title: '文件管理',
      icon: <AdminPanelSettingsIcon />,
      path: '/admin/documents',
      roles: ['admin'],
    },
    {
      title: '影片下載管理',
      icon: <VideoLibraryIcon />,
      path: '/video-download',
      roles: ['admin'],
    },
    {
      title: '系統設定',
      icon: <SettingsIcon />,
      path: '/admin?tab=5',
      roles: ['admin'],
    },
  ];

  // 檢查使用者是否有權限看到該選單項目
  const hasPermission = (item) => {
    if (!user) return false;
    return item.roles.includes(user.role);
  };

  // 渲染選單項目
  const renderMenuItem = (item, index) => {
    if (!hasPermission(item)) return null;

    return (
      <ListItem key={index} disablePadding>
        <ListItemButton
          onClick={() => handleMenuClick(item.path)}
          sx={{
            '&:hover': {
              backgroundColor: 'rgba(139, 69, 19, 0.08)',
            },
          }}
        >
          <ListItemIcon sx={{ color: 'primary.main' }}>{item.icon}</ListItemIcon>
          <ListItemText primary={item.title} />
        </ListItemButton>
      </ListItem>
    );
  };

  // 側邊欄內容
  const drawerContent = (
    <Box>
      {/* 使用者資訊區域 */}
      {user && (
        <Box
          sx={{
            p: 2,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
          }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            {user.username}
          </Typography>
          <Typography variant="caption">{getRoleLabel(user.role)}</Typography>
        </Box>
      )}

      {/* 個人設定選單 */}
      {user && (
        <List>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => setChangePasswordOpen(true)}
              sx={{
                '&:hover': {
                  backgroundColor: 'rgba(139, 69, 19, 0.08)',
                },
              }}
            >
              <ListItemIcon sx={{ color: 'primary.main' }}>
                <VpnKeyIcon />
              </ListItemIcon>
              <ListItemText primary="修改密碼" />
            </ListItemButton>
          </ListItem>
        </List>
      )}

      <Divider />

      {/* 基本功能選單 */}
      <List>
        <ListItem>
          <Typography variant="overline" color="text.secondary">
            基本功能
          </Typography>
        </ListItem>
        {menuItems.map((item, index) => renderMenuItem(item, index))}
      </List>

      {/* 進階功能選單 */}
      {user && (user.role === 'advanced' || user.role === 'admin') && (
        <>
          <Divider />
          <List>
            <ListItem>
              <Typography variant="overline" color="text.secondary">
                進階功能
              </Typography>
            </ListItem>
            {advancedItems.map((item, index) => renderMenuItem(item, `adv-${index}`))}
          </List>
        </>
      )}

      {/* 管理功能選單 */}
      {user && user.role === 'admin' && (
        <>
          <Divider />
          <List>
            <ListItem>
              <Typography variant="overline" color="text.secondary">
                管理功能
              </Typography>
            </ListItem>
            {adminItems.map((item, index) => renderMenuItem(item, `admin-${index}`))}
          </List>
        </>
      )}

      {/* 未登入提示 */}
      {!user && (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            請先登入以使用完整功能
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <>
      <Drawer
        variant={variant}
        open={open}
        onClose={onClose}
        ModalProps={{
          keepMounted: true, // 提升手機性能
        }}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            marginTop: variant === 'permanent' ? '64px' : 0, // permanent 模式下避開 AppBar
            height: variant === 'permanent' ? 'calc(100vh - 64px)' : '100vh', // 調整高度
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* 修改密碼對話框 */}
      <ChangePasswordDialog
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />
    </>
  );
};

export default Sidebar;
